import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useToast } from "../components/Toast";
import RatingStars from "../components/RatingStars";
import { useI18n } from "../context/I18nContext";
import { api } from "../utils/api";
import { isPhotoImage } from "../utils/images";

const CATEGORY_CLASSES = {
  healthy: "tag-healthy",
  tasty: "tag-tasty",
  balanced: "tag-balanced",
};

export default function MealDetail({ meal, navigate }) {
  const { state, actions } = useApp();
  const { toast } = useToast();
  const { language, t } = useI18n();
  const [addedIngredients, setAddedIngredients] = useState(new Set());
  const [currentMeal, setCurrentMeal] = useState(meal);
  const [consumption, setConsumption] = useState(null);
  const [servings, setServings] = useState("1");
  const [consumptionBusy, setConsumptionBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportForm, setReportForm] = useState({
    reporterName: "",
    reporterEmail: "",
    issueItems: "",
    details: "",
    referenceUrl: ""
  });

  useEffect(() => {
    setCurrentMeal(meal);
    setAddedIngredients(new Set());
  }, [meal]);

  useEffect(() => {
    if (!meal?.id) return;
    api.getMeal(meal.id, language).then((response) => setCurrentMeal(response.data)).catch(() => {});
  }, [meal?.id, language]);

  useEffect(() => {
    if (!meal?.id || !state.user?.id) {
      setConsumption(null);
      return;
    }
    actions.getMealConsumption(meal.id).then((data) => {
      setConsumption(data);
      if (data?.servings) setServings(String(data.servings));
    }).catch(() => setConsumption(null));
  }, [meal?.id, state.user?.id]);

  if (!currentMeal) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🍽️</div>
        <h3>{t("noMealSelected")}</h3>
        <button className="btn btn-primary" onClick={() => navigate("meals")}>{t("backToMeals")}</button>
      </div>
    );
  }

  const isFav = state.favoriteIds.includes(currentMeal.id);
  const categoryClass = CATEGORY_CLASSES[currentMeal.category] || CATEGORY_CLASSES.balanced;
  const backPage = currentMeal.mealType === "dessert" ? "desserts" : "meals";
  const categoryLabel = currentMeal.localized?.category || t(currentMeal.category || "balanced");
  const mealTypeLabel = currentMeal.localized?.mealType || t(currentMeal.mealType || "meal");
  const userRecipeVideoUrl = currentMeal.source === "user" && currentMeal.youtubeUrl && currentMeal.youtubeId
    ? currentMeal.youtubeUrl
    : "";
  const externalReference = currentMeal.sourceReferences?.[0] || null;
  const externalSourceLabel = currentMeal.externalSourceProvider || externalReference?.title || "";
  const externalSourceUrl = currentMeal.externalSourceUrl || externalReference?.url || "";
  const ingredientRows = Array.isArray(currentMeal.detailedIngredients) && currentMeal.detailedIngredients.length
    ? currentMeal.detailedIngredients.map((item) => item.display || item.name).filter(Boolean)
    : currentMeal.ingredients;
  const visibleSteps = Array.isArray(currentMeal.detailedSteps) && currentMeal.detailedSteps.length
    ? currentMeal.detailedSteps
    : currentMeal.steps;
  const nutrition = currentMeal.nutritionEstimate;
  const nutritionTotal = currentMeal.nutritionTotalEstimate;
  const handleBack = () => navigate(backPage);

  const handleFav = async () => {
    const added = await actions.toggleFavorite(currentMeal.id);
    toast(added ? t("favoriteAdded") : t("favoriteRemoved"), added ? "success" : "default");
  };

  const handleAddAllToShopping = async () => {
    let added = 0;
    for (const ingredient of ingredientRows) {
      const result = await actions.addToShopping(ingredient);
      if (result.success) added++;
    }
    setAddedIngredients(new Set(ingredientRows));
    toast(`${added} ${t("ingredientsAddedToShopping")}`, "success");
  };

  const handleAddOneToShopping = async (ingredient) => {
    const result = await actions.addToShopping(ingredient);
    if (result.success) {
      setAddedIngredients((previous) => new Set([...previous, ingredient]));
      toast(`"${ingredient}" ${t("ingredientAdded")}`, "success");
    } else {
      toast(result.error || t("alreadyOnList"), "error");
    }
  };

  const handleConsumption = async () => {
    if (!state.user?.id) {
      toast(t("mealLogLoginRequired"), "error");
      return;
    }
    setConsumptionBusy(true);
    try {
      if (consumption) {
        await actions.unmarkMealConsumed(currentMeal.id);
        setConsumption(null);
        toast(t("mealLogRemoved"), "default");
      } else {
        const saved = await actions.markMealConsumed(currentMeal.id, { servings });
        setConsumption(saved);
        toast(t("mealLogged"), "success");
      }
    } catch (error) {
      toast(error.message || t("requestFailed"), "error");
    } finally {
      setConsumptionBusy(false);
    }
  };

  const updateReport = (key, value) => {
    setReportForm((previous) => ({ ...previous, [key]: value }));
  };

  const submitCopyrightReport = async (event) => {
    event.preventDefault();
    if (!reportForm.reporterEmail.trim() || !reportForm.issueItems.trim()) {
      toast(t("copyrightReportRequired"), "error");
      return;
    }
    setReporting(true);
    try {
      await api.reportCopyright(currentMeal.id, reportForm);
      toast(t("copyrightReportSubmitted"), "success");
      setReportOpen(false);
      setReportForm({ reporterName: "", reporterEmail: "", issueItems: "", details: "", referenceUrl: "" });
    } catch (error) {
      toast(error.message || t("requestFailed"), "error");
    } finally {
      setReporting(false);
    }
  };

  return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={handleBack}>
        ← {t("back")}
      </button>

      <div className="detail-hero">
        <div className="detail-hero-top">
          <div className="detail-media">
            <div className={`detail-emoji ${isPhotoImage(currentMeal.image) ? "has-photo" : ""}`}>
              {isPhotoImage(currentMeal.image) ? <img src={currentMeal.image} alt={currentMeal.title} /> : currentMeal.image}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setReportOpen(true)}>
              {t("copyrightReportButton")}
            </button>
          </div>
          <div className="detail-hero-info">
            <h1>{currentMeal.title}</h1>
            <p className="detail-description">{currentMeal.description}</p>
            <div className="detail-tags">
              <span className="tag tag-cuisine">{currentMeal.localized?.cuisine || currentMeal.cuisine}</span>
              <span className={`tag ${categoryClass}`}>{categoryLabel}</span>
              <span className="tag tag-meal">{mealTypeLabel}</span>
              {currentMeal.dessertType && <span className="tag tag-dessert">{currentMeal.localized?.dessertType || currentMeal.dessertType}</span>}
            </div>
            {externalSourceLabel ? (
              <p className="recipe-source-line">
                <strong>{t("recipeSource")}:</strong>{" "}
                {externalSourceUrl ? (
                  <a href={externalSourceUrl} target="_blank" rel="noreferrer">{externalSourceLabel}</a>
                ) : (
                  <span>{externalSourceLabel}</span>
                )}
                {currentMeal.externalImageImported === false ? <span> {t("sourceImageNotImported")}</span> : null}
              </p>
            ) : null}
            <div className="detail-stats">
              <div className="detail-stat"><span>{t("prepTime")}</span><strong>⏱ {currentMeal.prepTime} {t("minutes")}</strong></div>
              <div className="detail-stat"><span>{t("cookTime")}</span><strong>🍳 {currentMeal.cookTime} {t("minutes")}</strong></div>
              <div className="detail-stat"><span>{t("calories")}</span><strong>{Number(currentMeal.calories) > 0 ? `🔥 ${currentMeal.calories} ${t("kcal")}` : t("nutritionUnverified")}</strong></div>
              <div className="detail-stat"><span>{t("servings")}</span><strong>{Number(currentMeal.servings) > 0 ? currentMeal.servings : t("nutritionUnverified")}</strong></div>
            </div>
            {nutrition ? (
              <div className="recipe-nutrition-box">
                <div className="recipe-nutrition-head">
                  <strong>{t("nutritionSummary")}</strong>
                  <span>
                    {nutrition.source === "usda_fooddata_central_user_recipe_calculated"
                      ? t("calculatedPerServing")
                      : nutrition.source === "usda_fooddata_central_reviewed"
                        ? t("verifiedPerServing")
                        : t("estimatedPerServing")}
                  </span>
                </div>
                <div className="recipe-macro-grid">
                  <span>{t("protein")}: <strong>{nutrition.proteinG} g</strong></span>
                  <span>{t("carbs")}: <strong>{nutrition.carbsG} g</strong></span>
                  <span>{t("fat")}: <strong>{nutrition.fatG} g</strong></span>
                  <span>{t("fiber")}: <strong>{nutrition.fiberG ?? "-"} g</strong></span>
                </div>
              </div>
            ) : null}
            {nutritionTotal ? (
              <div className="recipe-nutrition-box">
                <div className="recipe-nutrition-head">
                  <strong>{t("wholeRecipeNutritionSummary")}</strong>
                  <span>{t("calculatedWholeRecipe")}</span>
                </div>
                <div className="recipe-macro-grid">
                  <span>{t("calories")}: <strong>{nutritionTotal.calories} {t("kcal")}</strong></span>
                  <span>{t("protein")}: <strong>{nutritionTotal.proteinG} g</strong></span>
                  <span>{t("carbs")}: <strong>{nutritionTotal.carbsG} g</strong></span>
                  <span>{t("fat")}: <strong>{nutritionTotal.fatG} g</strong></span>
                  <span>{t("fiber")}: <strong>{nutritionTotal.fiberG ?? "-"} g</strong></span>
                </div>
                <p className="muted">{t("yieldNotProvided")}</p>
              </div>
            ) : null}
            <RatingStars meal={currentMeal} onRated={setCurrentMeal} />
            {["labels", "queued"].includes(currentMeal.localized?.translationStatus) && language !== "tr" && (
              <p className="muted">
                {currentMeal.localized?.translationStatus === "queued" ? t("translationQueued") : t("translationNeedsKey")}
              </p>
            )}
            <div className="detail-actions">
              <button className="btn btn-primary" onClick={handleFav}>
                {isFav ? `♥ ${t("favoriteRemove")}` : `♡ ${t("favoriteAdd")}`}
              </button>
              <button className="btn btn-green" onClick={handleAddAllToShopping}>
                🛒 {t("addAllIngredients")}
              </button>
            </div>
            <div className="meal-log-box">
              <div>
                <strong>{consumption ? t("mealLoggedToday") : t("markMealEaten")}</strong>
                <span>{t("mealLogHint")}</span>
              </div>
              <select value={servings} onChange={(event) => setServings(event.target.value)} disabled={Boolean(consumption)}>
                <option value="0.5">0.5 {t("servings")}</option>
                <option value="1">1 {t("servings")}</option>
                <option value="1.5">1.5 {t("servings")}</option>
                <option value="2">2 {t("servings")}</option>
              </select>
              <button className={`btn ${consumption ? "btn-outline" : "btn-primary"} btn-sm`} onClick={handleConsumption} disabled={consumptionBusy}>
                {consumptionBusy ? t("wait") : consumption ? t("undoMealLog") : t("iAteThis")}
              </button>
            </div>
          </div>
        </div>

        <div className="detail-body">
          <div className="detail-section">
            <h2>🥕 {t("ingredients")}</h2>
            <ul className="ingredient-list">
              {ingredientRows.map((ingredient, index) => {
                const isAdded = addedIngredients.has(ingredient);
                return (
                  <li key={`${ingredient}-${index}`} className="ingredient-item ingredient-item-row">
                    <span className="ingredient-bullet">•</span>
                    <span style={{ flex: 1 }}>{ingredient}</span>
                    <button
                      className={`ingredient-add-btn ${isAdded ? "added" : ""}`}
                      onClick={() => handleAddOneToShopping(ingredient)}
                      disabled={isAdded}
                    >
                      {isAdded ? t("added") : t("addToList")}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="detail-section">
            <h2>👨‍🍳 {t("steps")}</h2>
            <ol className="step-list">
              {visibleSteps.map((step, index) => (
                <li key={index} className="step-item">
                  <span className="step-num">{index + 1}</span>
                  <span className="step-text">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {userRecipeVideoUrl && (
        <div className="video-section">
          <h2>🎬 {t("videoRecipe")}</h2>
          <div
            className="video-thumb"
            onClick={() => window.open(userRecipeVideoUrl, "_blank")}
          >
            <img
              src={`https://img.youtube.com/vi/${currentMeal.youtubeId}/hqdefault.jpg`}
              alt={currentMeal.title}
              onError={(event) => { event.target.style.display = "none"; }}
            />
            <div className="video-play-overlay">
              <div className="video-play-circle">▶</div>
              <div className="video-play-text">YouTube</div>
            </div>
          </div>
        </div>
      )}

      {reportOpen && (
        <div className="modal-backdrop">
          <form className="modal-card recipe-form" onSubmit={submitCopyrightReport}>
            <div className="modal-header">
              <strong>{t("copyrightReportTitle")}</strong>
              <button type="button" onClick={() => setReportOpen(false)}>x</button>
            </div>
            <p className="muted">{t("copyrightReportReviewNote")}</p>
            <label>
              {t("copyrightReporterName")}
              <input value={reportForm.reporterName} onChange={(event) => updateReport("reporterName", event.target.value)} />
            </label>
            <label>
              {t("copyrightReporterEmail")}
              <input type="email" value={reportForm.reporterEmail} onChange={(event) => updateReport("reporterEmail", event.target.value)} required />
            </label>
            <label>
              {t("copyrightIssueItems")}
              <textarea value={reportForm.issueItems} onChange={(event) => updateReport("issueItems", event.target.value)} placeholder={t("copyrightIssueItemsPlaceholder")} required />
            </label>
            <label>
              {t("copyrightReferenceUrl")}
              <input type="url" value={reportForm.referenceUrl} onChange={(event) => updateReport("referenceUrl", event.target.value)} placeholder="https://..." />
            </label>
            <label>
              {t("copyrightDetails")}
              <textarea value={reportForm.details} onChange={(event) => updateReport("details", event.target.value)} placeholder={t("copyrightDetailsPlaceholder")} />
            </label>
            <button className="btn btn-primary" type="submit" disabled={reporting}>
              {reporting ? t("saving") : t("copyrightReportSubmit")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
